import type { QueueStorage, StoredQueueSnapshot } from '@layers/client';

const STORAGE_KEY_PREFIX = 'layers_queue_';

let _AsyncStorage: any = undefined;
let _initPromise: Promise<any> | null = null;

function getAsyncStorage() {
  if (_AsyncStorage) return Promise.resolve(_AsyncStorage);

  if (!_initPromise) {
    _initPromise = import('@react-native-async-storage/async-storage')
      .then((module) => {
        _AsyncStorage = module.default || module;
        return _AsyncStorage;
      })
      .catch(() => {
        return null;
      });
  }

  return _initPromise;
}

const createMemoryQueueStorage = (): QueueStorage => {
  let snapshot: StoredQueueSnapshot | null = null;
  return {
    async load() {
      return snapshot;
    },
    async save(nextSnapshot: StoredQueueSnapshot) {
      snapshot = nextSnapshot;
    },
    async clear() {
      snapshot = null;
    }
  };
};

export function createReactNativeQueueStorage(appId: string): QueueStorage {
  const storageKey = `${STORAGE_KEY_PREFIX}${appId}`;
  const memoryFallback = createMemoryQueueStorage();
  let useMemory = false;
  let hasWarned = false;

  const getStorageOrFallback = async () => {
    if (useMemory) return null;

    const AsyncStorage = await getAsyncStorage();
    if (!AsyncStorage) {
      useMemory = true;
      if (!hasWarned) {
        console.warn(
          '[Layers] AsyncStorage not found. Falling back to in-memory storage. Events will not persist across restarts.'
        );
        hasWarned = true;
      }
      return null;
    }
    return AsyncStorage;
  };

  return {
    async load() {
      const AsyncStorage = await getStorageOrFallback();
      if (!AsyncStorage) return memoryFallback.load();

      try {
        const value = await AsyncStorage.getItem(storageKey);
        if (!value) {
          return null;
        }
        return JSON.parse(value) as StoredQueueSnapshot;
      } catch (error) {
        console.warn('[Layers] Failed to load queue from AsyncStorage', error);
        return null;
      }
    },
    async save(snapshot: StoredQueueSnapshot) {
      const AsyncStorage = await getStorageOrFallback();
      if (!AsyncStorage) return memoryFallback.save(snapshot);

      try {
        await AsyncStorage.setItem(storageKey, JSON.stringify(snapshot));
      } catch (error) {
        console.warn('[Layers] Failed to persist queue to AsyncStorage', error);
      }
    },
    async clear() {
      const AsyncStorage = await getStorageOrFallback();
      if (!AsyncStorage) return memoryFallback.clear();

      try {
        await AsyncStorage.removeItem(storageKey);
      } catch (error) {
        console.warn('[Layers] Failed to clear queue storage', error);
      }
    }
  };
}
